import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { ShoppingCart, FileText, Truck, ChevronDown, ChevronUp, Trash2, Mail, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const OrdersTab = ({ orders, onUpdateStatus, onDeleteOrder, onRefresh }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trackingDialog, setTrackingDialog] = useState({ open: false, orderId: null, status: null });
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  const handleMigrateEmails = async () => {
    setIsMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-order-emails`);
      toast.success(`Updated ${response.data.updated_count} orders with customer emails`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to migrate emails');
    } finally {
      setIsMigrating(false);
    }
  };

  // Filter counts
  const filterCounts = useMemo(() => {
    const counts = { all: orders.length };
    STATUS_FILTERS.slice(1).forEach(filter => {
      counts[filter.key] = orders.filter(o => o.status === filter.key).length;
    });
    return counts;
  }, [orders]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    if (activeFilter === 'all') return orders;
    return orders.filter(o => o.status === activeFilter);
  }, [orders, activeFilter]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-emerald-100 text-emerald-700';
      case 'shipped': return 'bg-emerald-100 text-emerald-700';
      case 'processing': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}/invoice`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderId.slice(0,8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleStatusChange = (orderId, newStatus) => {
    if (newStatus === 'shipped') {
      setTrackingDialog({ open: true, orderId, status: newStatus });
      setTrackingNumber('');
    } else {
      onUpdateStatus(orderId, newStatus);
    }
  };

  const handleTrackingSubmit = async () => {
    if (!trackingDialog.orderId) return;
    setIsSubmitting(true);
    try {
      await onUpdateStatus(trackingDialog.orderId, trackingDialog.status, trackingNumber.trim() || null);
      setTrackingDialog({ open: false, orderId: null, status: null });
      setTrackingNumber('');
      toast.success('Order marked as shipped' + (trackingNumber ? ' with tracking number' : ''));
    } catch (error) {
      toast.error('Failed to update order status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipTracking = async () => {
    setIsSubmitting(true);
    try {
      await onUpdateStatus(trackingDialog.orderId, trackingDialog.status, null);
      setTrackingDialog({ open: false, orderId: null, status: null });
      setTrackingNumber('');
    } catch (error) {
      toast.error('Failed to update order status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      if (onDeleteOrder) {
        await onDeleteOrder(orderId);
      } else {
        toast.error('Delete function not available');
      }
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (orders.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl">
        <ShoppingCart className="w-12 h-12 text-text-muted mx-auto mb-4" />
        <p className="text-text-secondary">No orders yet</p>
      </div>
    );
  }

  return (
    <>
      {/* Header with Filter Tabs and Migration Button */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="orders-filter-tabs">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === filter.key
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
            data-testid={`filter-${filter.key}`}
          >
            {filter.label} ({filterCounts[filter.key] || 0})
          </button>
        ))}
        </div>

        {/* Fix Emails Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleMigrateEmails}
          disabled={isMigrating}
          className="rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isMigrating ? 'animate-spin' : ''}`} />
          {isMigrating ? 'Fixing...' : 'Fix Order Emails'}
        </Button>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="orders-tab">
        {/* Table Header */}
        <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b text-sm font-medium text-gray-500 uppercase tracking-wider">
          <div className="col-span-1"></div>
          <div className="col-span-2">Order ID</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Total</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Status</div>
        </div>

        {/* Order Rows */}
        <div className="divide-y divide-gray-100">
          {filteredOrders.map((order) => {
            const isExpanded = expandedOrder === order.id;
            const customerName = order.customer_name 
              ? `${order.customer_name.first_name || ''} ${order.customer_name.last_name || ''}`.trim()
              : 'Customer';
            const customerEmail = order.customer_email || '';

            return (
              <div key={order.id} data-testid={`order-card-${order.id}`}>
                {/* Main Row */}
                <div 
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleExpand(order.id)}
                >
                  {/* Expand Toggle */}
                  <div className="hidden md:flex col-span-1 items-center">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>

                  {/* Order ID */}
                  <div className="col-span-2 font-medium text-gray-900">
                    #{order.id.slice(0, 8).toUpperCase()}
                  </div>

                  {/* Customer */}
                  <div className="col-span-3">
                    <div className="font-medium text-gray-900">{customerName}</div>
                    <div className="text-sm text-gray-500">{customerEmail}</div>
                  </div>

                  {/* Total */}
                  <div className="col-span-2 font-semibold text-gray-900">
                    ${order.total?.toFixed(2)}
                  </div>

                  {/* Date */}
                  <div className="col-span-2 text-gray-600">
                    {formatDate(order.created_at)}
                  </div>

                  {/* Status & Actions */}
                  <div className="col-span-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Select 
                      value={order.status} 
                      onValueChange={(value) => handleStatusChange(order.id, value)}
                    >
                      <SelectTrigger 
                        className={`w-[120px] rounded-lg border-0 ${getStatusColor(order.status)}`}
                        data-testid={`status-select-${order.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <button 
                      onClick={() => handleDeleteOrder(order.id)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete order"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 py-6 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {/* Items Section */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Items ({order.items?.length || 0})
                        </h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-700">
                                {item.product_name} x{item.quantity}
                              </span>
                              <span className="text-gray-600">${item.subtotal?.toFixed(2)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                            <span className="text-gray-600">Shipping</span>
                            <span className="text-gray-600">${order.shipping?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between text-sm font-semibold">
                            <span>Total</span>
                            <span className="text-emerald-600">${order.total?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Shipping Address
                        </h4>
                        {order.shipping_address ? (
                          <div className="text-sm text-gray-700 space-y-1">
                            <p className="font-medium">{customerName}</p>
                            <p>{order.shipping_address.street}</p>
                            <p>
                              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                            </p>
                            <p>{order.shipping_address.country}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No address provided</p>
                        )}
                      </div>

                      {/* Contact & Actions */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Contact
                        </h4>
                        <div className="text-sm text-gray-700 space-y-1 mb-4">
                          {customerEmail && <p>{customerEmail}</p>}
                          {order.phone && <p>{order.phone}</p>}
                          {order.payment_status === 'paid' && (
                            <p className="text-emerald-600">Payment: Stripe</p>
                          )}
                          {order.tracking_number && (
                            <p className="text-blue-600 flex items-center gap-1">
                              <Truck className="w-4 h-4" />
                              {order.tracking_number}
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="w-full justify-start rounded-lg"
                            onClick={() => handleDownloadInvoice(order.id)}
                            data-testid={`invoice-btn-${order.id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" /> Download Invoice
                          </Button>
                          {customerEmail && (
                            <Button 
                              size="sm"
                              className="w-full justify-start rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white"
                              onClick={() => window.location.href = `mailto:${customerEmail}`}
                            >
                              <Mail className="w-4 h-4 mr-2" /> Email Customer
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredOrders.length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500">
            No orders with status "{activeFilter}"
          </div>
        )}
      </div>

      {/* Tracking Number Dialog */}
      <Dialog open={trackingDialog.open} onOpenChange={(open) => !open && setTrackingDialog({ open: false, orderId: null, status: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Add Tracking Number
            </DialogTitle>
            <DialogDescription>
              Enter the tracking number for this shipment. The customer will receive an email with tracking information.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g., 1Z999AA10123456784"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="rounded-xl"
              data-testid="tracking-number-input"
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleSkipTracking}
              disabled={isSubmitting}
              className="rounded-full"
            >
              Skip
            </Button>
            <Button
              onClick={handleTrackingSubmit}
              disabled={isSubmitting}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="submit-tracking-btn"
            >
              {isSubmitting ? 'Updating...' : 'Mark as Shipped'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrdersTab;
